import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { formatUTCDateToEastern } from '../utils/dateUtils';
import { 
  ServerIcon, 
  CalendarIcon, 
  GlobeAltIcon,
  CurrencyDollarIcon,
  XCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const DashboardPage = () => {
  const { user } = useAuth();
  const [hostingPlans, setHostingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageAction, setManageAction] = useState(''); // 'billing' or 'plan'
  const [newBillingCycle, setNewBillingCycle] = useState('');
  const [newPlanType, setNewPlanType] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [changeSummary, setChangeSummary] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [planToCancel, setPlanToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState({});
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(null);

  useEffect(() => {
    fetchHostingPlans();
  }, []);

  const fetchHostingPlans = async () => {
    try {
      const response = await api.get('/api/hosting/plans');
      setHostingPlans(response.data.plans);
    } catch (error) {
      toast.error('Failed to load hosting plans');
    } finally {
      setLoading(false);
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'shared':
        return '🏠';
      case 'private':
        return '🏢';
      case 'premium':
        return '⭐';
      default:
        return '🌐';
    }
  };

  const getPlanFeatures = (planType) => {
    const features = {
      shared: [
        'White-Glove Onboarding & Migration',
        'Domain Registration Assistance',
        'Free SSL & Daily Malware Scans',
        'Downtime Monitoring',
        'Basic Support via Email',
        'Plesk Dashboard Access'
      ],
      private: [
        'All Shared features plus:',
        'Dedicated Resources (RAM, CPU)',
        'Enhanced Security & Privacy',
        'Priority Email Support',
        'Daily Backups'
      ],
      premium: [
        'All Private features plus:',
        'Advanced CloudFlare Caching',
        'High Priority Phone & Email support',
        'First Year Free Domain Registration',
        '1 Hour/Month of Dedicated Server Troubleshooting'
      ]
    };
    return features[planType] || [];
  };

  const getPlanPrice = (planType, billingCycle) => {
    const prices = {
      shared: { monthly: 20, yearly: 200 },
      private: { monthly: 25, yearly: 250 },
      premium: { monthly: 40, yearly: 400 }
    };
    return prices[planType]?.[billingCycle] || 0;
  };

  const openManageModal = (plan, action) => {
    setSelectedPlan(plan);
    setManageAction(action);
    setNewBillingCycle(action === 'billing' ? plan.billingCycle : '');
    setNewPlanType(action === 'plan' ? plan.planType : '');
    setShowManageModal(true);
  };

  const calculateChangeSummary = async () => {
    if (!selectedPlan) return;

    try {
      const response = await api.post('/api/hosting/calculate-changes', {
        planId: selectedPlan._id,
        action: manageAction,
        newBillingCycle: manageAction === 'billing' ? newBillingCycle : selectedPlan.billingCycle,
        newPlanType: manageAction === 'plan' ? newPlanType : selectedPlan.planType
      });

      setChangeSummary(response.data);
      setShowConfirmation(true);
    } catch (error) {
      toast.error('Failed to calculate changes');
    }
  };

  const applyPlanChange = async () => {
    if (!selectedPlan || !newPlanType || !newBillingCycle) {
      setError('Please select a plan and new configuration');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await api.post('/api/hosting/apply-plan-change', {
        planId: selectedPlan._id,
        newPlanType,
        newBillingCycle
      });

      const data = response.data;

      if (data.success) {
        setSuccess('Plan change applied successfully!');
        setShowManageModal(false);
        setShowConfirmation(false);
        setSelectedPlan(null);
        setNewPlanType('');
        setNewBillingCycle('');
        setChangeSummary(null);
        
        // Refresh plans
        fetchHostingPlans();
        
        // Show refund info if applicable
        if (data.refund) {
          setSuccess(`Plan change applied successfully! Refund of $${(data.refund.amount / 100).toFixed(2)} has been processed.`);
        }
      } else {
        setError(data.error || 'Failed to apply plan change');
      }
    } catch (error) {
      console.error('Error applying plan change:', error);
      setError(error.response?.data?.error || 'Failed to apply plan change');
    } finally {
      setProcessing(false);
    }
  };

  // UTC from API → display in Eastern (EST/EDT)
  const formatDate = (date) => formatUTCDateToEastern(date, { long: true });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const fetchPaymentHistory = async (planId) => {
    if (paymentHistory[planId]) {
      setPaymentHistoryOpen(paymentHistoryOpen === planId ? null : planId);
      return;
    }
    setPaymentHistoryLoading(planId);
    try {
      const response = await api.get(`/api/hosting/plans/${planId}/payment-history`);
      setPaymentHistory(prev => ({ ...prev, [planId]: response.data.payments || [] }));
      setPaymentHistoryOpen(planId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load payment history');
    } finally {
      setPaymentHistoryLoading(null);
    }
  };

  const formatPaymentStatus = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'paid') return { label: 'Paid', className: 'text-green-600' };
    if (s === 'payment_pending' || s === 'pending') return { label: 'Pending', className: 'text-amber-600' };
    if (s === 'partially_paid') return { label: 'Partial', className: 'text-blue-600' };
    return { label: status || '—', className: 'text-gray-600' };
  };

  // End of current period = start + 1 period (same as next billing date)
  const calculateExpirationDate = (startDate, billingCycle) => {
    const endOfPeriod = new Date(startDate);
    if (billingCycle === 'monthly') {
      endOfPeriod.setMonth(endOfPeriod.getMonth() + 1);
    } else if (billingCycle === 'yearly') {
      endOfPeriod.setFullYear(endOfPeriod.getFullYear() + 1);
    }
    return endOfPeriod;
  };

  // Expiration date = when the plan ends (use next billing date when cancelling; otherwise period end)
  const getExpirationDate = (plan) => {
    if (plan.cancelAtPeriodEnd && plan.nextBillingDate) {
      return new Date(plan.nextBillingDate);
    }
    return calculateExpirationDate(plan.createdAt, plan.billingCycle);
  };

  const handleCancelClick = (plan) => {
    setPlanToCancel(plan);
    setShowCancelModal(true);
  };

  const handleCancelSubscription = async () => {
    if (!planToCancel) return;

    setCancelling(true);
    try {
      const response = await api.put(`/api/hosting/cancel-plan/${planToCancel._id}`, {
        reason: 'User requested cancellation'
      });

      toast.success(response.data.message || 'Subscription will be cancelled at the end of the current period');
      setShowCancelModal(false);
      setPlanToCancel(null);
      
      // Refresh plans
      fetchHostingPlans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-2">
              Welcome back, {user?.firstName}! Manage your hosting plans and account.
            </p>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <XCircleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setSuccess(null)}
                    className="text-green-400 hover:text-green-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hosting Plans */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Your Hosting Plans</h2>
            
            {hostingPlans.length === 0 ? (
              <div className="text-center py-12">
                <ServerIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hosting plans yet</h3>
                <p className="text-gray-600 mb-6">
                  Get started by purchasing your first hosting plan.
                </p>
                <a
                  href="/"
                  className="btn-primary"
                >
                  Browse Plans
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hostingPlans.map((plan) => (
                  <div
                    key={plan._id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{getPlanIcon(plan.planType)}</span>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 capitalize">
                            {plan.planType} Hosting
                          </h3>
                          <p className="text-sm text-gray-600 capitalize">
                            {plan.billingCycle} billing
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(plan.price)}
                        </p>
                        <p className="text-sm text-gray-500 capitalize">
                          per {plan.billingCycle}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-sm text-gray-600">
                        <GlobeAltIcon className="h-4 w-4 mr-2" />
                        <span className="truncate">{plan.url || 'No URL set'}</span>
                      </div>
                      {/* Next billing only when plan is not expiring; when expiring, show only Expires */}
                      {!(plan.cancelAtPeriodEnd || plan.status === 'cancelled') && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          <span>Next billing: {formatDate(plan.nextBillingDate)}</span>
                        </div>
                      )}
                      {(plan.cancelAtPeriodEnd || plan.status === 'cancelled') && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          <span>Expires: {formatDate(getExpirationDate(plan))}</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600">
                        <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                        <span className="capitalize">{plan.status}</span>
                      </div>
                      {plan.cancelAtPeriodEnd && (
                        <div className="flex items-center text-sm text-orange-600 bg-orange-50 p-2 rounded">
                          <XCircleIcon className="h-4 w-4 mr-2" />
                          <span>Cancellation scheduled - expires {formatDate(getExpirationDate(plan))}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col space-y-2">
                      {plan.status === 'active' && !plan.cancelAtPeriodEnd && (
                        <button
                          onClick={() => handleCancelClick(plan)}
                          className="w-full btn-secondary text-sm text-red-600 hover:bg-red-50 border-red-200"
                        >
                          Cancel Subscription
                        </button>
                      )}
                      <button
                        onClick={() => fetchPaymentHistory(plan._id)}
                        disabled={!plan.squareSubscriptionId || paymentHistoryLoading === plan._id}
                        className="w-full btn-secondary text-sm flex items-center justify-center gap-2"
                      >
                        {paymentHistoryLoading === plan._id ? (
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                        ) : (
                          <BanknotesIcon className="h-4 w-4" />
                        )}
                        Payment history
                        {paymentHistoryOpen === plan._id ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                      {paymentHistoryOpen === plan._id && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Payments (from Square)</h4>
                          {(paymentHistory[plan._id] || []).length === 0 ? (
                            <p className="text-sm text-gray-500">No payments yet for this subscription.</p>
                          ) : (
                            <ul className="space-y-2 text-sm">
                              {(paymentHistory[plan._id] || []).map((p) => {
                                const statusInfo = formatPaymentStatus(p.status);
                                return (
                                  <li
                                    key={p.id}
                                    className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50"
                                  >
                                    <span className="text-gray-600">
                                      {p.createdAt ? formatDate(p.createdAt) : '—'}
                                    </span>
                                    <span className="font-medium">{formatCurrency((p.amountCents || 0) / 100)}</span>
                                    <span className={statusInfo.className}>{statusInfo.label}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manage Plan Modal */}
      {showManageModal && selectedPlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {manageAction === 'billing' ? 'Change Billing Cycle' : 'Change Plan Type'}
              </h3>

              {manageAction === 'billing' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Billing Cycle
                    </label>
                    <select
                      value={newBillingCycle}
                      onChange={(e) => setNewBillingCycle(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Select billing cycle</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Plan Type
                    </label>
                    <select
                      value={newPlanType}
                      onChange={(e) => setNewPlanType(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Select plan type</option>
                      <option value="shared">Shared Hosting</option>
                      <option value="private">Private Hosting</option>
                      <option value="premium">Premium Hosting</option>
                    </select>
                  </div>
                  
                  {/* Plan comparison */}
                  {newPlanType && newPlanType !== selectedPlan.planType && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Plan Comparison</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Current Plan:</span>
                          <span className="capitalize">{selectedPlan.planType} - {formatCurrency(getPlanPrice(selectedPlan.planType, selectedPlan.billingCycle))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>New Plan:</span>
                          <span className="capitalize">{newPlanType} - {formatCurrency(getPlanPrice(newPlanType, selectedPlan.billingCycle))}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowManageModal(false);
                    setChangeSummary(null);
                  }}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={calculateChangeSummary}
                  disabled={!newBillingCycle && !newPlanType}
                  className="flex-1 btn-primary"
                >
                  Calculate Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && changeSummary && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Changes</h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Change Summary</h4>
                  <div className="space-y-2 text-sm text-blue-800">
                    <div className="flex justify-between">
                      <span>New Plan:</span>
                      <span className="capitalize">{changeSummary.newPlanType} ({changeSummary.newBillingCycle})</span>
                    </div>
                    <div className="flex justify-between">
                      <span>New Price:</span>
                      <span>{formatCurrency(changeSummary.newPrice)}</span>
                    </div>
                    {changeSummary.refundAmount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Refund Amount:</span>
                        <span>{formatCurrency(changeSummary.refundAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Next Billing:</span>
                      <span>{formatDate(changeSummary.nextBillingDate)}</span>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p>• Your current plan will be cancelled and a prorated refund will be issued</p>
                  <p>• The new plan will be activated immediately</p>
                  <p>• You'll be charged the new amount on the next billing date</p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 btn-secondary"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={applyPlanChange}
                  disabled={processing}
                  className="flex-1 btn-primary flex items-center justify-center"
                >
                  {processing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Confirm Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && planToCancel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Cancel Subscription</h3>
              
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">Important Information</h4>
                  <div className="space-y-2 text-sm text-yellow-800">
                    <p>• Your subscription will remain active until the end of your current billing period.</p>
                    <p>• Your site will expire on: <strong>{formatDate(planToCancel.nextBillingDate)}</strong></p>
                    <p>• You will continue to have access to all features until that date.</p>
                    <p>• No refunds will be issued for the remaining period.</p>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Plan:</strong> {planToCancel.planType.charAt(0).toUpperCase() + planToCancel.planType.slice(1)} Hosting ({planToCancel.billingCycle})</p>
                  <p><strong>Subscription Started:</strong> {formatDate(planToCancel.createdAt)}</p>
                  <p><strong>Expiration Date:</strong> {formatDate(planToCancel.nextBillingDate)}</p>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setPlanToCancel(null);
                  }}
                  className="flex-1 btn-secondary"
                  disabled={cancelling}
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="flex-1 btn-primary flex items-center justify-center bg-red-600 hover:bg-red-700"
                >
                  {cancelling ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Cancel Subscription'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardPage; 